// crewbit-admin/src/components/schedule/MonthCanvasContainer.tsx
//
// Schedule Module Phase C C3 — sibling-component extract of Month-canvas
// wiring (admin; mirrors DayCanvasContainer + WeekCanvasContainer pattern
// from C1.6/C2 per Golden Rule #14 surface-thinning).
//
// Industry Engine Phase 1c — recurring-badge gating now derives from the
// cluster resolver (`config.recurringBias`), keyed on the threaded `categoryId`.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §4 + §C.6.

import { useCallback, useState } from 'react';
import { useRescheduleScheduleEntry } from '../../hooks/useRescheduleScheduleEntry';
import { useResolvedClusterConfig } from '../../hooks/useResolvedClusterConfig';
import { useBusinessSchedulePreferences } from '../../hooks/useBusinessSchedulePreferences';
import { useLeaseBoundariesByMonth } from '../../hooks/useLeaseBoundariesByMonth';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { ConflictDetail } from '../../lib/errorMap';
import type { ScheduleEntry } from '../../types/schedule';
import { useAuthStore } from '../../stores/authStore';
import { instantToBusinessParts, localWallClockToInstant } from './scheduleHelpers';
import { MonthCanvas } from './MonthCanvas';

export interface MonthCanvasContainerProps {
  effectiveAnchor: Date;
  entries: ScheduleEntry[];
  business: { id?: string; industry?: string | null } | null;
  /** The business's 17-category id — threaded down for the cluster resolver. */
  categoryId?: string | null;
  onCellTap: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  refresh: () => void;
  onToast: (toast: { msg: string; type: 'success' | 'error' }) => void;
}

export function MonthCanvasContainer({
  effectiveAnchor,
  entries,
  business,
  categoryId,
  onCellTap,
  onPrevMonth,
  onNextMonth,
  onToday,
  refresh,
  onToast,
}: MonthCanvasContainerProps) {
  const { config: clusterConfig } = useResolvedClusterConfig(categoryId);
  const { data: businessSchedulePreferences } = useBusinessSchedulePreferences();
  const dateIso = effectiveAnchor.toISOString().split('T')[0];
  const { byDate: leaseBoundariesByDate } = useLeaseBoundariesByMonth({
    monthStart: dateIso,
    businessId: business?.id,
    enabled: businessSchedulePreferences?.lease_end_markers_enabled === true,
  });

  const rescheduleEntry = useRescheduleScheduleEntry();
  const [serverConflicts, setServerConflicts] = useState<ConflictDetail[] | null>(null);
  const [pendingReschedule, setPendingReschedule] = useState<{
    entryId: string;
    newStartAt: string;
    newEndAt: string;
  } | null>(null);

  const handleCellDrop = useCallback(async (newDate: string, droppedEntryId: string) => {
    setServerConflicts(null);
    const entry = entries.find((e) => e.id === droppedEntryId);
    if (!entry) return;
    // Cross-cell drop = date change with time preserved per spec §4.8.
    // A2 P4 — preserve the BUSINESS-tz wall-clock when moving to newDate, then
    // rebuild true instants (the old device-tz setFullYear drifted the time on a
    // device whose tz differs from the business tz, and could land on the wrong day).
    const tz = useAuthStore.getState().business?.timezone ?? null;
    const s = instantToBusinessParts(entry.start_at, tz);
    const e = instantToBusinessParts(entry.end_at, tz);
    const pad = (n: number) => String(n).padStart(2, '0');
    const newStartAt = localWallClockToInstant(newDate, `${pad(s.hour)}:${pad(s.minute)}:00`, tz);
    const newEndAt = localWallClockToInstant(newDate, `${pad(e.hour)}:${pad(e.minute)}:00`, tz);
    const result = await rescheduleEntry({
      entryId: droppedEntryId,
      newStartAt,
      newEndAt,
      force: false,
    });
    if (!result.success) {
      if (result.error.code === 'CONFLICT_DETECTED' && result.error.conflicts) {
        setServerConflicts(result.error.conflicts);
        setPendingReschedule({ entryId: droppedEntryId, newStartAt, newEndAt });
      } else {
        onToast({ msg: lookupErrorCopy('reschedule_schedule_entry', result.error.code), type: 'error' });
      }
      return;
    }
    refresh();
    onToast({ msg: 'Rescheduled', type: 'success' });
  }, [entries, rescheduleEntry, refresh, onToast]);

  const handleOverride = useCallback(async () => {
    if (!pendingReschedule) return;
    const result = await rescheduleEntry({
      entryId: pendingReschedule.entryId,
      newStartAt: pendingReschedule.newStartAt,
      newEndAt: pendingReschedule.newEndAt,
      force: true,
    });
    setServerConflicts(null);
    setPendingReschedule(null);
    if (!result.success) {
      onToast({ msg: lookupErrorCopy('reschedule_schedule_entry', result.error.code), type: 'error' });
      return;
    }
    refresh();
    onToast({ msg: 'Rescheduled (override)', type: 'success' });
  }, [pendingReschedule, rescheduleEntry, refresh, onToast]);

  return (
    <MonthCanvas
      date={dateIso}
      entries={entries}
      leaseBoundariesByDate={leaseBoundariesByDate}
      showRecurringBadges={clusterConfig.recurringBias}
      onCellTap={onCellTap}
      onCellDrop={handleCellDrop}
      conflicts={serverConflicts ?? undefined}
      conflictRpcName="reschedule_schedule_entry"
      onConflictOverride={handleOverride}
      onConflictDismiss={() => { setServerConflicts(null); setPendingReschedule(null); }}
      onPrevMonth={onPrevMonth}
      onNextMonth={onNextMonth}
      onToday={onToday}
    />
  );
}
